import { MapPin, Search } from 'lucide-react'
import { useCallback, useMemo, type ReactNode } from 'react'
import { useAppStore } from '@core/store'
import { isFeatureEnabled } from '@core/features'
import JanelaDestacada from '@core/layout/janela-destacada/JanelaDestacada'
import { pesoDaPrioridade } from '@modules/bcf/lib/bcfAgrupar'
import { facetaDaEtiqueta } from '@modules/bcf/lib/facetasDasEtiquetas'
import {
  alternarNaFaceta,
  contagensFacetadas,
  contarGrupos,
  contarOrigens,
  contarPorCampo,
  countByStatus,
  escolhidosDaFaceta,
  filterPins,
  filtrosActivos,
  resumoDosFiltros,
  rotuloDoValor,
  ROTULO_FACETA_DOS_PINS,
  semCadaFiltro,
  type FacetaDosPins,
} from './lib/pinFilter'
import { CHAVE_JANELA_PINS, TAMANHO_MINIMO, TAMANHO_POR_OMISSAO } from './lib/janelaDosPins'
import JanelaNo3D from '@viewer/lib/JanelaNo3D'
import { CLASSES_DO_TOM, ROTULO_DO_TOM, type TomDaMarca } from './lib/pinBolhas'
import { usePinFiltro } from './usePinFiltro'

const TITULO_SECCAO = 'mb-1 text-[10px] uppercase tracking-wide text-[var(--color-text-dim)]'

/** Um valor que se escolhe: premido filtra por ele. Com zero pins (com os
 *  outros filtros), fica esbatido mas continua a poder escolher-se. */
function Escolha({
  premido,
  onClick,
  disabled,
  total,
  children,
  title,
}: {
  premido: boolean
  onClick: () => void
  disabled?: boolean
  total: number
  children: ReactNode
  title?: string
}) {
  return (
    <button
      type="button"
      aria-pressed={premido}
      disabled={disabled}
      title={title}
      onClick={onClick}
      data-total={total}
      className={`inline-flex max-w-full items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] disabled:opacity-50 ${
        premido
          ? 'border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
          : `border-[var(--color-border)] hover:bg-[var(--color-surface)] ${total === 0 ? 'opacity-50' : ''}`
      }`}
    >
      <span className="truncate">{children}</span>
      <span className="text-[10px] text-[var(--color-text-muted)]">{total}</span>
    </button>
  )
}

const ORDEM_DA_LEGENDA: TomDaMarca[] = ['critica', 'alta', 'normal', 'baixa', 'sem-prioridade', 'fechado']

/** Os valores de uma faceta pela ordem fixa (a dos totais, para os chips
 *  não saltarem a cada clique), com os escolhidos que já não existem nos
 *  pins no fim (senão ficavam a filtrar sem chip para os desmarcar). */
function comEscolhidos(valores: string[], escolhidos: string[]): string[] {
  const vistos = new Set(valores)
  return [...valores, ...escolhidos.filter((e) => !vistos.has(e))]
}

/** "N de M pins" do painel e da barra minimizada. */
function useContagem() {
  const visible = useAppStore((s) => s.bcfPinsVisible)
  const topics = useAppStore((s) => s.bcfPinTopics)
  const filtro = usePinFiltro()
  const aMostrar = useMemo(() => (visible ? filterPins(topics, filtro).length : 0), [visible, topics, filtro])
  return { aMostrar, total: topics.length }
}

/** Controlo dos pins BCF sobre o modelo.
 *
 *  Existe por causa de uma queixa concreta: os pins apareciam sobre o
 *  3D (um por conflito triado em BCF) e não havia por onde os desligar
 *  nem escolher quais mostrar. Um marcador que não se consegue tirar
 *  deixa de ser informação e passa a ser estorvo.
 *
 *  Filtros por facetas (pesquisa, prioridade, estado, grupo,
 *  especialidade, nível, outras etiquetas, responsável e origem): OU
 *  dentro de cada faceta, E entre facetas, marcado = só esses. As
 *  contagens ao lado de cada valor já contam com os outros filtros
 *  (contagens facetadas), e com zero pins o painel diz que filtro deixa a
 *  combinação a zero. A pesquisa, a prioridade, as etiquetas e o
 *  responsável aplicam-se com a função da lista do módulo BCF
 *  (`topicoPassaFiltros`).
 *
 *  A janela arrasta-se, muda de tamanho, minimiza-se e destaca-se para
 *  uma janela do browser (o mesmo store: os pins e as bolhas no 3D da
 *  janela principal seguem os filtros escolhidos lá). */
export default function BcfPinsPanel() {
  const open = useAppStore((s) => s.bcfPinsPanelOpen)
  const destacado = useAppStore((s) => s.bcfPinsPanelDestacado)
  const setDestacado = useAppStore((s) => s.setBcfPinsPanelDestacado)
  const setOpen = useAppStore((s) => s.setBcfPinsPanelOpen)
  // Estáveis: a janela destacada guarda-os e não pode reabrir por causa
  // de uma identidade nova.
  const voltarAoPainel = useCallback(() => setDestacado(false), [setDestacado])
  const fechar = useCallback(() => setOpen(false), [setOpen])

  if (!open || !isFeatureEnabled('bcfPins')) return null

  if (destacado) {
    return (
      <JanelaDestacada
        aberta
        nome="vn2r-bcf-pins"
        titulo="Pins BCF"
        features="width=420,height=820,resizable=yes,scrollbars=yes"
        oQue="o painel dos pins"
        aoVoltar={voltarAoPainel}
        aoFechar={fechar}
      >
        <div data-testid="pins-destacado" className="text-xs">
          <ContagemDosPins className="block border-b border-[var(--color-border)] px-3 py-1.5" />
          <CorpoDosPins />
        </div>
      </JanelaDestacada>
    )
  }

  return <JanelaDosPins />
}

function ContagemDosPins({ className }: { className?: string }) {
  const { aMostrar, total } = useContagem()
  return (
    <span className={`text-[10px] text-[var(--color-text-muted)] ${className ?? ''}`} data-campo="contagem">
      {aMostrar} de {total} pins
    </span>
  )
}

/** A janela flutuante dentro da área do 3D (`JanelaNo3D`, que nasceu
 *  aqui e o Mapa também usa). */
function JanelaDosPins() {
  const setOpen = useAppStore((s) => s.setBcfPinsPanelOpen)
  const setDestacado = useAppStore((s) => s.setBcfPinsPanelDestacado)
  const { aMostrar, total } = useContagem()
  const contagem = (
    <span className="text-[10px] text-[var(--color-text-muted)]" data-campo="contagem">
      {aMostrar} de {total} pins
    </span>
  )
  return (
    <JanelaNo3D
      titulo="Pins BCF"
      icone={<MapPin size={15} className="text-[var(--color-accent)]" />}
      chave={CHAVE_JANELA_PINS}
      tamanhoPorOmissao={TAMANHO_POR_OMISSAO}
      tamanhoMinimo={TAMANHO_MINIMO}
      canto="inferior-esquerdo"
      camada="pins-bcf"
      prefixo="pins"
      oQue="o painel dos pins"
      naBarra={contagem}
      naBarraMinimizada={contagem}
      aoDestacar={() => setDestacado(true)}
      aoFechar={() => setOpen(false)}
      dicaMinimizar="os pins continuam como estão"
    >
      <CorpoDosPins />
    </JanelaNo3D>
  )
}

/** Os filtros e as opções de desenho: o mesmo no painel flutuante e na
 *  janela destacada. */
function CorpoDosPins() {
  const visible = useAppStore((s) => s.bcfPinsVisible)
  const setVisible = useAppStore((s) => s.setBcfPinsVisible)
  const topics = useAppStore((s) => s.bcfPinTopics)
  const hideClosed = useAppStore((s) => s.bcfPinsHideClosed)
  const setHideClosed = useAppStore((s) => s.setBcfPinsHideClosed)
  const seguirModulo = useAppStore((s) => s.bcfPinsSeguirModulo)
  const setSeguirModulo = useAppStore((s) => s.setBcfPinsSeguirModulo)
  const criterios = useAppStore((s) => s.bcfPinsCriterios)
  const setCriterios = useAppStore((s) => s.setBcfPinsCriterios)
  const limparFiltros = useAppStore((s) => s.limparFiltrosDosPins)
  const esconderTapados = useAppStore((s) => s.bcfPinsEsconderTapados)
  const setEsconderTapados = useAppStore((s) => s.setBcfPinsEsconderTapados)
  const esconderForaDoCorte = useAppStore((s) => s.bcfPinsEsconderForaDoCorte)
  const setEsconderForaDoCorte = useAppStore((s) => s.setBcfPinsEsconderForaDoCorte)
  const resumo = useAppStore((s) => s.bcfPinsResumoVista)
  const vista = useAppStore((s) => s.bcfVistaDaLista)
  const nomeDaLista = useAppStore((s) =>
    s.bcfVistaDaLista ? (s.bcfLoaded.find((b) => b.localId === s.bcfVistaDaLista!.bcfLocalId)?.name ?? null) : null,
  )
  const { aMostrar } = useContagem()

  const entrada = useMemo(
    () => ({ hideClosed, seguirModulo, vista, criterios }),
    [hideClosed, seguirModulo, vista, criterios],
  )
  const contagens = useMemo(() => contagensFacetadas(topics, entrada), [topics, entrada])
  const semCada = useMemo(
    () => (visible && topics.length > 0 && aMostrar === 0 ? semCadaFiltro(topics, entrada) : []),
    [visible, topics, aMostrar, entrada],
  )

  // Os valores de cada faceta, pela ordem dos totais (fixa enquanto os
  // pins não mudam).
  const valores = useMemo(() => {
    const prioridades = contarPorCampo(topics, 'priority')
      .sort(
        (a, b) =>
          (a.valor === '' ? 1 : 0) - (b.valor === '' ? 1 : 0) ||
          pesoDaPrioridade(b.valor) - pesoDaPrioridade(a.valor) ||
          a.valor.localeCompare(b.valor, 'pt'),
      )
      .map((p) => p.valor)
    const etiquetas = contarPorCampo(topics, 'labels').map((e) => e.valor)
    return {
      prioridade: prioridades,
      estado: countByStatus(topics).map((e) => e.status),
      grupo: contarGrupos(topics).map((g) => g.chave),
      especialidade: etiquetas.filter((e) => facetaDaEtiqueta(e) === 'especialidade'),
      nivel: etiquetas
        .filter((e) => facetaDaEtiqueta(e) === 'nivel')
        .sort((a, b) => a.localeCompare(b, 'pt', { numeric: true })),
      outras: etiquetas.filter((e) => e !== '' && facetaDaEtiqueta(e) === 'outras'),
      responsavel: contarPorCampo(topics, 'assignedTo').map((r) => r.valor),
      origem: contarOrigens(topics).map((o) => o.valor),
    } satisfies Record<FacetaDosPins, string[]>
  }, [topics])
  const estadosFechados = useMemo(
    () => new Set(countByStatus(topics).filter((e) => e.closed).map((e) => e.status)),
    [topics],
  )
  const nomesDosGrupos = useMemo(() => new Map(contarGrupos(topics).map((g) => [g.chave, g.nome])), [topics])
  const nomeDoGrupo = (chave: string) => nomesDosGrupos.get(chave) ?? chave

  const nFiltros = filtrosActivos(criterios, hideClosed)
  const resumoDosActivos = resumoDosFiltros(criterios, hideClosed, nomeDoGrupo)

  const alternar = (f: FacetaDosPins, valor: string) => setCriterios(alternarNaFaceta(criterios, f, valor))

  /** Uma faceta em chips, com a contagem facetada de cada valor. */
  const chips = (f: FacetaDosPins, titulo?: (v: string) => string | undefined) => {
    const escolhidos = escolhidosDaFaceta(criterios, f)
    const lista = comEscolhidos(valores[f], escolhidos)
    if (lista.length === 0) return null
    return (
      <div data-campo={f}>
        <div className={TITULO_SECCAO}>{ROTULO_FACETA_DOS_PINS[f]}</div>
        <div className="flex max-h-28 flex-wrap gap-1 overflow-y-auto">
          {lista.map((v) => (
            <Escolha
              key={v}
              premido={escolhidos.includes(v)}
              onClick={() => alternar(f, v)}
              disabled={!visible}
              total={contagens[f].get(v) ?? 0}
              title={titulo?.(v)}
            >
              {rotuloDoValor(f, v)}
            </Escolha>
          ))}
        </div>
      </div>
    )
  }

  const gruposEscolhidos = escolhidosDaFaceta(criterios, 'grupo')
  const listaDeGrupos = comEscolhidos(valores.grupo, gruposEscolhidos)

  return (
    <div className="space-y-3 p-3">
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={visible}
          onChange={(e) => setVisible(e.target.checked)}
          className="accent-[var(--color-accent)]"
        />
        Mostrar pins sobre o modelo
      </label>

      <label
        className="flex items-start gap-2"
        title="Os pins mostram só os tópicos que a lista do módulo BCF mostra com os filtros actuais e, com Agrupar por, só os dos blocos abertos. Os filtros deste painel aplicam-se por cima."
      >
        <input
          type="checkbox"
          checked={seguirModulo}
          onChange={(e) => setSeguirModulo(e.target.checked)}
          disabled={!visible}
          className="mt-0.5 accent-[var(--color-accent)]"
        />
        <span>
          Seguir os filtros do módulo BCF
          <span className="block text-[10px] text-[var(--color-text-muted)]" data-campo="lista-do-modulo">
            {!seguirModulo
              ? 'Desligado: os filtros do módulo não contam.'
              : vista
                ? `A lista de "${nomeDaLista ?? 'BCF'}" mostra ${vista.guids.length} tópico${vista.guids.length === 1 ? '' : 's'}; os pins de outros BCF ficam escondidos.`
                : 'A lista do módulo BCF não está aberta: não há filtros a seguir.'}
          </span>
        </span>
      </label>

      {topics.length === 0 ? (
        <p className="text-[var(--color-text-muted)]">
          Não há pins. Um tópico ganha pin quando tem um viewpoint com
          posição: os capturados na plataforma e os dos conflitos trazem-na;
          nos importados de um .bcfzip calcula-se a partir dos elementos
          seleccionados nos modelos abertos ou, sem eles, da câmara.
        </p>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <div
              className={TITULO_SECCAO}
              data-campo="n-filtros"
              title={
                nFiltros > 0
                  ? `${nFiltros} filtro${nFiltros === 1 ? '' : 's'} activo${nFiltros === 1 ? '' : 's'} (conta-se cada critério uma vez, por muitos valores que tenha marcados).`
                  : undefined
              }
            >
              Filtros{nFiltros > 0 ? ` (${nFiltros})` : ''}
            </div>
            <button
              type="button"
              className="mb-1 ml-auto rounded border border-[var(--color-border)] px-2 py-0.5 hover:bg-[var(--color-surface)] disabled:opacity-50"
              disabled={!visible || nFiltros === 0}
              onClick={limparFiltros}
            >
              Limpar filtros
            </button>
          </div>

          {resumoDosActivos.length > 0 && (
            <ul className="-mt-2 space-y-0.5 text-[10px] text-[var(--color-text-muted)]" data-campo="resumo-filtros">
              {resumoDosActivos.map((l) => (
                <li key={l} className="truncate" title={l}>
                  {l}
                </li>
              ))}
            </ul>
          )}

          {visible && aMostrar === 0 && (
            <div
              role="status"
              data-campo="porque-zero"
              className="rounded border border-[var(--color-warn-border)] bg-[var(--color-warn-bg)] px-2 py-1.5 text-[11px] text-[var(--color-warn-text)]"
            >
              <strong>Nenhum pin passa os filtros.</strong>{' '}
              {semCada.length > 0 ? (
                <>
                  Sem um destes filtros já passavam pins:
                  <ul className="mt-0.5 list-disc pl-4">
                    {semCada.map((s) => (
                      <li key={s.rotulo}>
                        sem {s.rotulo}: {s.passariam} pin{s.passariam === 1 ? '' : 's'}
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                'Só tirando dois ou mais filtros passa algum pin.'
              )}
            </div>
          )}

          <p className="text-[10px] text-[var(--color-text-muted)]">
            Marcado = só esses. Dentro de cada critério basta um dos
            marcados; entre critérios, têm de passar todos. O número ao lado
            de cada valor já conta com os outros filtros: 0 quer dizer que
            esse valor não tem pins em comum com o resto da escolha.
          </p>

          <label className="flex items-center gap-1.5 rounded border border-[var(--color-border)] px-2 py-1">
            <Search size={12} className="text-[var(--color-text-muted)]" aria-hidden="true" />
            <input
              type="search"
              value={criterios.pesquisa}
              onChange={(e) => setCriterios({ pesquisa: e.target.value })}
              disabled={!visible}
              placeholder="Procurar no título ou na descrição"
              aria-label="Procurar nos tópicos dos pins"
              className="min-w-0 flex-1 bg-transparent outline-none"
            />
          </label>

          {chips('prioridade')}

          <div>
            <label className="mb-1 flex items-center gap-2">
              <input
                type="checkbox"
                checked={hideClosed}
                onChange={(e) => setHideClosed(e.target.checked)}
                disabled={!visible}
                className="accent-[var(--color-accent)]"
              />
              Esconder os fechados e resolvidos
            </label>
            {chips('estado', (v) =>
              estadosFechados.has(v) ? 'Estado terminal (fechado ou resolvido) segundo o ficheiro BCF.' : undefined,
            )}
          </div>

          {listaDeGrupos.length > 0 && (
            <div data-campo="grupo">
              <div className={TITULO_SECCAO}>{ROTULO_FACETA_DOS_PINS.grupo}</div>
              <ul className="max-h-32 space-y-1 overflow-y-auto pr-1">
                {listaDeGrupos.map((chave) => {
                  const n = contagens.grupo.get(chave) ?? 0
                  return (
                    <li key={chave}>
                      <label
                        className={`flex items-center gap-2 ${n === 0 && !gruposEscolhidos.includes(chave) ? 'opacity-50' : ''}`}
                        title={nomeDoGrupo(chave)}
                      >
                        <input
                          type="checkbox"
                          checked={gruposEscolhidos.includes(chave)}
                          onChange={() => alternar('grupo', chave)}
                          disabled={!visible}
                          className="accent-[var(--color-accent)]"
                        />
                        <span className="truncate">{nomeDoGrupo(chave)}</span>
                        <span className="ml-auto text-[10px] text-[var(--color-text-muted)]">{n}</span>
                      </label>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}

          {chips('especialidade')}
          {chips('nivel')}
          {chips('outras')}
          {chips('responsavel')}
          {chips('origem')}

          <div className="space-y-1 border-t border-[var(--color-border)] pt-2">
            <div className={TITULO_SECCAO}>Desenho</div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={esconderTapados}
                onChange={(e) => setEsconderTapados(e.target.checked)}
                disabled={!visible}
                className="accent-[var(--color-accent)]"
              />
              Esconder os pins tapados
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={esconderForaDoCorte}
                onChange={(e) => setEsconderForaDoCorte(e.target.checked)}
                disabled={!visible}
                className="accent-[var(--color-accent)]"
              />
              Esconder os pins fora do corte
            </label>
            <p className="text-[10px] text-[var(--color-text-muted)]" data-campo="resumo-vista">
              {resumo.tapados} tapado{resumo.tapados === 1 ? '' : 's'}
              {esconderTapados ? ' (escondidos)' : ' (esbatidos)'}
              {resumo.foraDoCorte > 0
                ? ` · ${resumo.foraDoCorte} fora do corte${esconderForaDoCorte ? ' (escondidos)' : ' (esbatidos)'}`
                : ''}
              {resumo.bolhas > 0 ? ` · ${resumo.bolhas} bolha${resumo.bolhas === 1 ? '' : 's'}` : ''}
            </p>
            <p className="text-[10px] text-[var(--color-text-muted)]">
              Os tapados testam-se por raio quando a câmara pára. Os pins
              juntos no ecrã formam bolhas com o número de tópicos: clicar
              aproxima a câmara; se já não der para separar, abre a lista.
            </p>
            <div className="flex flex-wrap gap-x-2 gap-y-1 pt-1" aria-label="Legenda das cores">
              {ORDEM_DA_LEGENDA.map((tom) => (
                <span key={tom} className="inline-flex items-center gap-1 text-[10px] text-[var(--color-text-muted)]">
                  <span className={`h-2.5 w-2.5 rounded-full border ${CLASSES_DO_TOM[tom]}`} />
                  {ROTULO_DO_TOM[tom]}
                </span>
              ))}
            </div>
            <p className="text-[10px] text-[var(--color-text-muted)]">
              Cor: a prioridade mais alta dos tópicos abertos; verde quando
              estão todos fechados.
            </p>
          </div>
        </>
      )}
    </div>
  )
}
